function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import classnames from "classnames";
import Slider from "react-slick";
import throttle from "lodash.throttle";
import DefaultProps from "./default-props";
import { StyledCarousel } from "./carousel.scss";
const GO_TO_SPEED_MILLISECONDS = 500;
const GO_TO_ANIMATION_THROTTLE_DELAY = 100;
const PAUSE_AFTER_SWIPE_DELAY = 5000;
const ConditionalWrapper = ({ condition, wrapper, children })=>condition ? wrapper(children) : children;
const Carousel = /*#__PURE__*/ forwardRef((props, externalRef)=>{
    const slider = useRef(null);
    useImperativeHandle(externalRef, ()=>({
            scrollToSlide (index) {
                slider === null || slider === void 0 ? void 0 : slider.current.slickGoTo(index);
            }
        }), []);
    // Items props
    const { items, selectedIndex, itemRenderer, onSwipe, onChange, itemScale, lazyLoad } = props;
    // Carousel props
    const { autoplay, autoplaySpeed, infinite, slidesToShow, easing, arrows, nextArrow, prevArrow, dots, className, responsive } = props;
    // Advanced carousel props
    const { centerMode, centerPadding, defaultIndex, swipeToSlide, showFocusEffect, defaultImage, carouselClassName, onRefSet, focusAfterSwipe, disableSlideOnStartClick, speed, beforeChange, draggable } = props;
    if (!items) return null;
    // Arrange items in right format and order
    const defaultIndexNumber = Math.min(parseInt(defaultIndex), items.length);
    const hasDefault = defaultImage && defaultImage.src;
    let slides = [
        ...items
    ];
    if (hasDefault) {
        slides = [
            ...items.slice(0, defaultIndexNumber),
            defaultImage,
            ...items.slice(defaultIndexNumber)
        ];
    }
    const handleSwipe = (direction)=>{
        slider.current.slickPause();
        if (autoplay) {
            setTimeout(()=>slider.current.slickPlay(), PAUSE_AFTER_SWIPE_DELAY);
        }
        if (onSwipe) {
            onSwipe(direction);
        }
    };
    const handleChange = (current)=>{
        if (onChange) {
            onChange(current);
        }
        if (focusAfterSwipe) {
            var _slider_current_innerSlider_list, _slider_current_innerSlider, _slider_current;
            slider === null || slider === void 0 ? void 0 : (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : (_slider_current_innerSlider = _slider_current.innerSlider) === null || _slider_current_innerSlider === void 0 ? void 0 : (_slider_current_innerSlider_list = _slider_current_innerSlider.list) === null || _slider_current_innerSlider_list === void 0 ? void 0 : _slider_current_innerSlider_list.click();
        }
    };
    const focusSlider = ()=>{
        var _slider_current_innerSlider, _slider_current;
        if (slider === null || slider === void 0 ? void 0 : (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : (_slider_current_innerSlider = _slider_current.innerSlider) === null || _slider_current_innerSlider === void 0 ? void 0 : _slider_current_innerSlider.list) {
            slider.current.innerSlider.list.setAttribute("tabindex", 0);
            slider.current.innerSlider.list.focus();
        }
    };
    const settings = {
        // Default settings
        className: classnames({
            center: centerMode,
            focus: showFocusEffect,
            [className]: className
        }),
        speed: speed || GO_TO_SPEED_MILLISECONDS,
        dots,
        focusOnSelect: true,
        onSwipe: handleSwipe,
        afterChange: handleChange,
        beforeChange,
        // User props
        infinite,
        arrows,
        nextArrow,
        prevArrow,
        swipeToSlide,
        slidesToShow: Math.min(slidesToShow, slides.length),
        centerPadding,
        easing,
        centerMode,
        autoplay,
        autoplaySpeed: parseInt(autoplaySpeed),
        lazyLoad,
        draggable
    };
    const onIndexChanged = (selectedIndex)=>{
        let newIndex;
        if (selectedIndex !== null && centerMode) {
            newIndex = selectedIndex >= defaultIndexNumber && hasDefault ? selectedIndex + 1 : selectedIndex;
        } else {
            newIndex = defaultIndexNumber;
        }
        slider.current.slickGoTo(newIndex);
    };
    // Throttle and give some delay (100ms) because when the index changes
    // `slickGoTo` does not work properly when changing fast
    const throttledOnIndexChanged = useCallback(throttle(onIndexChanged, GO_TO_SPEED_MILLISECONDS + GO_TO_ANIMATION_THROTTLE_DELAY, {
        trailing: true
    }), []);
    useEffect(()=>{
        throttledOnIndexChanged(selectedIndex);
    }, [
        selectedIndex
    ]);
    useEffect(()=>{
        if (slider === null || slider === void 0 ? void 0 : slider.current) {
            if (onRefSet) {
                onRefSet(slider.current);
            }
        }
    }, [
        slider
    ]);
    return /*#__PURE__*/ _jsx(StyledCarousel, {
        className: classnames("carousel", carouselClassName),
        onTouchStart: disableSlideOnStartClick ? null : focusSlider,
        children: /*#__PURE__*/ _jsx(Slider, _object_spread_props(_object_spread({
            ref: slider
        }, settings), {
            initialSlide: defaultIndexNumber,
            responsive: responsive,
            children: slides.map((item, index)=>/*#__PURE__*/ _jsx("div", {
                    className: "carousel-item",
                    children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
                        condition: itemScale != 1,
                        wrapper: (children)=>/*#__PURE__*/ _jsx("div", {
                                style: {
                                    transform: `scale(${itemScale})`
                                },
                                children: children
                            }),
                        children: itemRenderer(item, index)
                    })
                }, `slide_${index}`))
        }))
    });
});
Carousel.defaultProps = DefaultProps;
export default Carousel;
